import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext';
import { useOutlet } from '../lib/OutletContext';
import { getIngredients, getIngredientLedger, getOpeningStockStatus, getTrialsSummary, syncTrialPurchases } from '../lib/api';
import { Package, TrendingUp, TrendingDown, AlertTriangle, AlertCircle, RefreshCw, ServerCrash, FlaskConical, ChevronDown, ChevronRight } from 'lucide-react';
import { formatCurrency, formatNumber, getRiskColor, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import OpeningStockWizard from '../components/OpeningStockWizard';
import IngredientsExportButton from '../components/IngredientsExportButton';
import logger from '../lib/logger';

const POLL_INTERVAL = 60000; // 60 seconds — lighter cadence; focus/visibility refresh fills the gap

/* Canonical provenance of a movement. `ingest_path === 'BILLS_NATIVE'` is the ONLY marker of Bills
   authority: the retired EC sync also wrote source_type VENDOR_BILL rows, and those stay historical. */
const movementSource = (entry) => {
  if (entry.ingest_path === 'BILLS_NATIVE') {
    return entry.source_type === 'EXPENSE' ? 'Bills · expense receipt' : 'Bills · supplier bill';
  }
  if (entry.source_type === 'VENDOR_BILL' || entry.source_type === 'EXPENSE'
      || entry.source_app === 'expense_capture' || entry.source === 'expense_capture') {
    return 'Expense Capture (historical)';
  }
  return 'Inventory';
};

const Ingredients = () => {
  const { user } = useAuth();

  // RBAC gate — mirrors backend `_user_can_mutate_inventory`:
  // ORG_ADMIN, SUPER_ADMIN, OUTLET_ADMIN, or PROCUREMENT_MANAGER (incl. substring fallback).
  const canInitializeInventory = (() => {
    if (!user) return false;
    if (user.is_org_admin || user.is_super_admin) return true;
    const pool = [user.role, user.platform_role_code, ...(user.roles || [])]
      .filter(Boolean).map((s) => String(s).toUpperCase());
    if (pool.some((r) => ['ORG_ADMIN', 'OUTLET_ADMIN', 'PROCUREMENT_MANAGER'].includes(r))) return true;
    return pool.some((r) => {
      const l = r.toLowerCase();
      return l.includes('procurement') && (l.includes('manager') || l.includes('mgr'));
    });
  })();
  const { selectedOutlet } = useOutlet();
  const navigate = useNavigate();
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIngredient, setSelectedIngredient] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openingStockStatus, setOpeningStockStatus] = useState(null);
  const [showWizard, setShowWizard] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [integrationError, setIntegrationError] = useState(null);
  const pollIntervalRef = useRef(null);
  
  // Trials tab state
  const [activeTab, setActiveTab] = useState('stock'); // 'stock' or 'trials'
  const [trialsSummary, setTrialsSummary] = useState(null);
  const [trialsLoading, setTrialsLoading] = useState(false);
  const [expandedTrialIngredient, setExpandedTrialIngredient] = useState(null);
  const [syncingTrials, setSyncingTrials] = useState(false);

  const loadIngredients = useCallback(async (showToast = false) => {
    if (!selectedOutlet) {
      logger.info('[Ingredients] No outlet selected');
      setLoading(false);
      return;
    }
    try {
      logger.info('[Ingredients] Loading for outlet:', selectedOutlet);
      const response = await getIngredients(selectedOutlet);
      
      // Handle new response format: { ingredients: [], count, sources, errors }
      const ingredientsList = response?.ingredients || response || [];
      setIngredients(Array.isArray(ingredientsList) ? ingredientsList : []);
      setLastUpdated(new Date());
      
      // Check for integration errors from backend
      if (response?.errors?.control_room) {
        setIntegrationError(`Control Room: ${response.errors.control_room}`);
      } else if (response?.errors?.pricing) {
        setIntegrationError(`Pricing unavailable: ${response.errors.pricing}`);
      } else if (ingredientsList.length === 0) {
        setIntegrationError('No ingredients returned. Control Room integration may be unavailable or ingredient master is empty.');
      } else {
        setIntegrationError(null);
      }
      
      if (showToast) toast.success('Data refreshed');
    } catch (error) {
      if (showToast) toast.error('Failed to refresh data');
      logger.error('[Ingredients] Error:', error);
      setIntegrationError(`Failed to load ingredients: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [selectedOutlet]);

  // Initial load
  useEffect(() => {
    if (selectedOutlet) {
      loadIngredients();
      checkOpeningStock();
    }
  }, [selectedOutlet, loadIngredients]);

  // Polling
  useEffect(() => {
    if (selectedOutlet && !loading) {
      setIsPolling(true);
      pollIntervalRef.current = setInterval(() => {
        loadIngredients();
      }, POLL_INTERVAL);
    }
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      setIsPolling(false);
    };
  }, [selectedOutlet, loading, loadIngredients]);

  // Refresh on window focus / tab visibility — fills the gap between
  // 60s polls so operators switching back to this tab see fresh data.
  useEffect(() => {
    if (!selectedOutlet) return undefined;
    const onFocus = () => loadIngredients();
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadIngredients();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [selectedOutlet, loadIngredients]);

  const checkOpeningStock = async () => {
    if (!selectedOutlet) return;
    setStatusLoading(true);
    try {
      const status = await getOpeningStockStatus(selectedOutlet);
      setOpeningStockStatus(status);
      logger.info('[Ingredients] Opening stock status:', status);
    } catch (error) {
      logger.error('[Ingredients] Failed to check opening stock status:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleOpeningStockSuccess = () => {
    loadIngredients();
    checkOpeningStock();
  };

  const handleManualRefresh = () => {
    loadIngredients(true);
  };

  // Trials functions
  const loadTrialsSummary = useCallback(async () => {
    if (!selectedOutlet) return;
    setTrialsLoading(true);
    try {
      const data = await getTrialsSummary(selectedOutlet);
      setTrialsSummary(data);
    } catch (error) {
      logger.error('[Ingredients] Trials summary error:', error);
      toast.error('Failed to load trials summary');
    } finally {
      setTrialsLoading(false);
    }
  }, [selectedOutlet]);

  const handleSyncTrials = async () => {
    if (!selectedOutlet) return;
    setSyncingTrials(true);
    try {
      const result = await syncTrialPurchases(selectedOutlet);
      toast.success(`Synced ${result.synced} trial purchases`);
      loadTrialsSummary();
    } catch (error) {
      logger.error('[Ingredients] Sync trials error:', error);
      toast.error('Failed to sync trial purchases');
    } finally {
      setSyncingTrials(false);
    }
  };

  // Load trials when tab changes
  useEffect(() => {
    if (activeTab === 'trials' && selectedOutlet) {
      loadTrialsSummary();
    }
  }, [activeTab, selectedOutlet, loadTrialsSummary]);

  const loadLedger = async (ingredient) => {
    if (!selectedOutlet) return;
    try {
      const data = await getIngredientLedger(ingredient.ingredient_id, selectedOutlet);
      setLedger(data);
      setSelectedIngredient(ingredient);
    } catch (error) {
      toast.error('Failed to load ledger');
      logger.error('[Ingredients] Ledger error:', error);
    }
  };

  const filteredIngredients = ingredients.filter((ing) =>
    ing.ingredient_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ingredients-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight mb-2" data-testid="ingredients-title">Ingredients</h1>
          <p className="text-zinc-400">Current stock on hand and inventory value</p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-zinc-500">
              Last updated: {formatDateTime(lastUpdated)}
              {isPolling && <span className="ml-2 text-emerald-500">● Live</span>}
            </span>
          )}
          <button
            onClick={handleManualRefresh}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#27272a] hover:bg-[#3f3f46] rounded-md transition-colors text-sm"
            data-testid="refresh-button"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Integration Error Banner */}
      {integrationError && ingredients.length === 0 && (
        <div
          className="flex items-start gap-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg"
          data-testid="integration-error-banner"
        >
          <ServerCrash className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-red-400 mb-1">Control Room Integration Issue</h3>
            <p className="text-sm text-red-200 mb-2">
              {integrationError}
            </p>
            <p className="text-xs text-zinc-400">
              Ingredients are sourced from Control Room. Check that the integration is properly configured and the canonical ingredients endpoint is accessible.
            </p>
          </div>
        </div>
      )}

      {/* Opening Stock Banner */}
      {!statusLoading && openingStockStatus && !openingStockStatus.is_initialized && (
        <div
          className="flex items-start gap-4 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg"
          data-testid="opening-stock-banner"
        >
          <AlertCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-amber-500 mb-1">Opening Stock Not Initialized</h3>
            <p className="text-sm text-amber-100 mb-3">
              Initialize physical stock to start tracking inventory accurately. This is a one-time operation.
            </p>
            {canInitializeInventory ? (
              <button
                onClick={() => setShowWizard(true)}
                className="px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-600 text-white font-semibold transition-colors text-sm"
                data-testid="initialize-opening-stock-button"
              >
                Initialize Opening Stock
              </button>
            ) : (
              <p className="text-xs text-amber-300/80 italic" data-testid="opening-stock-rbac-notice">
                Only admins or procurement managers can initialize inventory.
              </p>
            )}
          </div>
        </div>
      )}

      {!statusLoading && openingStockStatus && openingStockStatus.is_initialized && (
        <div className="space-y-2" data-testid="opening-stock-initialized-panel">
          <div className="flex items-center flex-wrap gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
            <Package className="w-5 h-5 text-emerald-500" />
            <span className="text-sm text-emerald-400">
              Opening Stock Initialized
              {openingStockStatus.opening_business_date && (
                <> as of <span className="font-mono">{openingStockStatus.opening_business_date}</span></>
              )}
              <span className="text-zinc-500"> · initialized on {formatDateTime(openingStockStatus.initialized_at)}</span>
            </span>
            {openingStockStatus.opening_stock_confidence && (
              <span
                className={
                  'inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ' +
                  (openingStockStatus.opening_stock_confidence === 'HIGH'
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
                    : openingStockStatus.opening_stock_confidence === 'MEDIUM'
                    ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                    : 'bg-amber-500/10 text-amber-400 border-amber-500/20')
                }
                data-testid="opening-stock-confidence-badge"
              >
                {openingStockStatus.opening_stock_confidence}
              </span>
            )}
          </div>
          {openingStockStatus.recommend_audit && (
            <div
              className="flex items-start gap-3 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg"
              data-testid="audit-recommendation-banner"
            >
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-amber-400 font-semibold mb-1">
                  Recommended: Perform physical audit to validate inventory baseline
                </p>
                <p className="text-xs text-amber-100">
                  Opening stock was initialized with LOW confidence and has not been audited for 10+ days.
                  An audit adjustment will additively correct the ledger without modifying opening stock.
                </p>
                <button
                  onClick={() => navigate('/audits')}
                  className="mt-2 text-xs font-semibold text-amber-400 hover:text-amber-300 underline"
                  data-testid="go-to-audits-button"
                >
                  Go to Audits →
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex items-center gap-1 border-b border-[#27272a]">
        <button
          onClick={() => setActiveTab('stock')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'stock'
              ? 'text-emerald-500 border-emerald-500'
              : 'text-zinc-400 border-transparent hover:text-zinc-200'
          }`}
          data-testid="stock-tab"
        >
          <Package className="w-4 h-4 inline mr-2" />
          Stock
        </button>
        <button
          onClick={() => setActiveTab('trials')}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
            activeTab === 'trials'
              ? 'text-purple-500 border-purple-500'
              : 'text-zinc-400 border-transparent hover:text-zinc-200'
          }`}
          data-testid="trials-tab"
        >
          <FlaskConical className="w-4 h-4 inline mr-2" />
          Trials
          {trialsSummary?.total_ingredients > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-xs bg-purple-500/20 text-purple-400 rounded">
              {trialsSummary.total_ingredients}
            </span>
          )}
        </button>
      </div>

      {/* Stock Tab Content */}
      {activeTab === 'stock' && (
        <>
          {/* Search + Export */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <input
              type="text"
              placeholder="Search ingredients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-96 px-4 py-2 bg-[#18181b] border border-[#3f3f46] rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
              data-testid="search-input"
            />
            <IngredientsExportButton
              ingredients={ingredients}
              filteredIngredients={filteredIngredients}
            />
          </div>

      {/* Opening Stock Wizard */}
      <OpeningStockWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onSuccess={handleOpeningStockSuccess}
        outletId={selectedOutlet}
      />

      {/* Ingredients Table */}
      <div className="bg-[#18181b] border border-[#27272a] rounded-lg overflow-hidden" data-testid="ingredients-table">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#09090b] border-b border-[#27272a]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Ingredient</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">SOH</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">Avg Cost</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">Value</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-zinc-400 uppercase tracking-wider">Risk</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-zinc-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#27272a]">
              {filteredIngredients.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center">
                    <Package className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
                    <p className="text-zinc-400 font-medium mb-2">No ingredients available</p>
                    <p className="text-sm text-zinc-500 max-w-md mx-auto">
                      {searchTerm 
                        ? `No ingredients matching "${searchTerm}"`
                        : "No ingredients found from Control Room. Check integration or initialize opening stock."}
                    </p>
                  </td>
                </tr>
              ) : (
                filteredIngredients.map((ingredient) => (
                  <tr
                    key={ingredient.ingredient_id}
                    className="hover:bg-[#09090b] transition-colors"
                    data-testid="ingredient-row"
                  >
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                          <Package className="w-4 h-4 text-emerald-500" />
                        </div>
                        <div>
                          <p className="font-medium" data-testid="ingredient-name">{ingredient.ingredient_name}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-mono font-semibold" data-testid="ingredient-soh">
                        {formatNumber(ingredient.soh)} {ingredient.base_unit}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex flex-col items-end">
                        <span className="font-mono text-sm text-zinc-400">
                          {formatCurrency(ingredient.avg_cost)}/{ingredient.base_unit}
                        </span>
                        {ingredient.pricing_source && ingredient.pricing_source !== 'NONE' && (
                          <span className={`text-xs mt-0.5 ${
                            ingredient.pricing_source === 'VENDOR' 
                              ? 'text-emerald-500' 
                              : ingredient.pricing_source === 'CANONICAL'
                                ? 'text-blue-500'
                                : ingredient.pricing_source === 'SEED'
                                  ? 'text-amber-500'
                                  : 'text-zinc-500'
                          }`}>
                            ({ingredient.pricing_source.toLowerCase()}{ingredient.pricing_period ? ` - ${ingredient.pricing_period}` : ''})
                          </span>
                        )}
                        {(ingredient.pricing_source === 'NONE' || !ingredient.avg_cost) && (
                          <span className="text-xs text-red-500 mt-0.5">No pricing</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="font-mono font-semibold" data-testid="ingredient-value">
                        {formatCurrency(ingredient.value)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskColor(ingredient.risk_flag)}`}
                        data-testid="ingredient-risk"
                      >
                        {ingredient.risk_flag === 'HIGH' && <AlertTriangle className="w-3 h-3 mr-1" />}
                        {ingredient.risk_flag}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      <Dialog>
                        <DialogTrigger asChild>
                          <button
                            onClick={() => loadLedger(ingredient)}
                            className="text-sm text-emerald-500 hover:text-emerald-400 font-medium"
                            data-testid="view-ledger-button"
                          >
                            View Ledger
                          </button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl bg-[#18181b] border-[#27272a] text-white">
                          <DialogHeader>
                            <DialogTitle className="text-xl font-bold">
                              {selectedIngredient?.ingredient_name} - Movement Ledger
                            </DialogTitle>
                          </DialogHeader>
                          <div className="mt-4 max-h-96 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-[#09090b] sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs text-zinc-400">Date</th>
                                  <th className="px-3 py-2 text-left text-xs text-zinc-400">Type</th>
                                  <th className="px-3 py-2 text-left text-xs text-zinc-400">Source</th>
                                  <th className="px-3 py-2 text-right text-xs text-zinc-400">Quantity</th>
                                  <th className="px-3 py-2 text-right text-xs text-zinc-400">Value</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-[#27272a]">
                                {ledger.map((entry, idx) => (
                                  <tr key={entry.ledger_id || `le-${idx}`} className="hover:bg-[#09090b]" data-testid={`ledger-row-${idx}`}>
                                    <td className="px-3 py-2 text-xs">
                                      {entry.occurred_at ? (
                                        <div>
                                          <div className="text-zinc-300" data-testid={`ledger-occurred-${idx}`}>
                                            {formatDateTime(entry.occurred_at)}
                                          </div>
                                          {entry.synced_at && entry.synced_at.slice(0, 10) !== entry.occurred_at.slice(0, 10) && (
                                            <div className="text-[10px] text-zinc-600" title={`Synced ${formatDateTime(entry.synced_at)}`}>
                                              synced {formatDateTime(entry.synced_at)}
                                            </div>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="text-amber-500/80" data-testid={`ledger-occurred-fallback-${idx}`} title="Upstream did not provide a business/event date for this row">
                                          {formatDateTime(entry.synced_at || entry.created_at)}
                                          <div className="text-[10px] text-zinc-500">occurred date unavailable</div>
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                      {entry.movement_type.replace(/_/g, ' ')}
                                    </td>
                                    <td className="px-3 py-2 text-xs" data-testid={`ledger-source-${idx}`}>
                                      {movementSource(entry)}
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono">
                                      <span className={entry.direction === 'IN' ? 'text-emerald-500' : 'text-red-500'}>
                                        {entry.direction === 'IN' ? '+' : '-'}{formatNumber(entry.quantity)}
                                      </span>
                                    </td>
                                    <td className="px-3 py-2 text-right font-mono text-zinc-400">
                                      {entry.total_value ? formatCurrency(entry.total_value) : '-'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}

      {/* Trials Tab Content */}
      {activeTab === 'trials' && (
        <div className="space-y-4">
          {/* Trials Header */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-zinc-400">
                Trial purchases are tracked separately to evaluate ingredients before regular procurement.
              </p>
            </div>
            <button
              onClick={handleSyncTrials}
              disabled={syncingTrials}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 rounded-md transition-colors text-sm font-medium"
              data-testid="sync-trials-button"
            >
              <RefreshCw className={`w-4 h-4 ${syncingTrials ? 'animate-spin' : ''}`} />
              {syncingTrials ? 'Syncing...' : 'Sync Trials'}
            </button>
          </div>

          {/* Trials Summary Cards */}
          {trialsSummary && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-1">Total Trial Ingredients</p>
                <p className="text-2xl font-bold text-purple-400">{trialsSummary.total_ingredients}</p>
              </div>
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-1">Total Trial Quantity</p>
                <p className="text-2xl font-bold">{formatNumber(trialsSummary.total_trial_qty)}</p>
              </div>
              <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-4">
                <p className="text-sm text-zinc-400 mb-1">Total Trial Value</p>
                <p className="text-2xl font-bold text-emerald-400">{formatCurrency(trialsSummary.total_trial_value)}</p>
              </div>
            </div>
          )}

          {/* Trials Table */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-lg overflow-hidden" data-testid="trials-table">
            {trialsLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
              </div>
            ) : !trialsSummary?.ingredients?.length ? (
              <div className="p-8 text-center">
                <FlaskConical className="w-12 h-12 mx-auto mb-3 text-zinc-600" />
                <p className="text-zinc-400 font-medium mb-2">No trial purchases yet</p>
                <p className="text-sm text-zinc-500">
                  Click &quot;Sync Trials&quot; to pull trial purchase data from Expense Capture.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#09090b] border-b border-[#27272a]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Ingredient</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">Trial Qty</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">Trial Value</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-zinc-400 uppercase tracking-wider">Bills</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-zinc-400 uppercase tracking-wider">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#27272a]">
                    {trialsSummary.ingredients.map((item) => (
                      <React.Fragment key={item.ingredient_id}>
                        <tr className="hover:bg-[#09090b] transition-colors">
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                                <FlaskConical className="w-4 h-4 text-purple-500" />
                              </div>
                              <div>
                                <p className="font-medium">{item.ingredient_name}</p>
                                <p className="text-xs text-zinc-500">{item.base_unit}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className="font-mono font-semibold text-purple-400">
                              {formatNumber(item.total_qty)} {item.base_unit}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-right">
                            <span className="font-mono text-sm text-zinc-400">
                              {formatCurrency(item.total_value)}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <span className="px-2 py-1 bg-zinc-800 rounded text-sm">
                              {item.bill_count}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-center">
                            <button
                              onClick={() => setExpandedTrialIngredient(
                                expandedTrialIngredient === item.ingredient_id ? null : item.ingredient_id
                              )}
                              className="text-sm text-purple-500 hover:text-purple-400 font-medium flex items-center gap-1 mx-auto"
                              data-testid="expand-trial-button"
                            >
                              {expandedTrialIngredient === item.ingredient_id ? (
                                <><ChevronDown className="w-4 h-4" /> Hide</>
                              ) : (
                                <><ChevronRight className="w-4 h-4" /> Show</>
                              )}
                            </button>
                          </td>
                        </tr>
                        {/* Drill-down row */}
                        {expandedTrialIngredient === item.ingredient_id && item.entries && (
                          <tr>
                            <td colSpan="5" className="px-4 py-2 bg-[#09090b]">
                              <div className="pl-8">
                                <p className="text-xs text-zinc-500 mb-2">Contributing Bills</p>
                                <table className="w-full text-sm">
                                  <thead>
                                    <tr className="text-xs text-zinc-500">
                                      <th className="text-left py-1">Bill ID</th>
                                      <th className="text-right py-1">Quantity</th>
                                      <th className="text-right py-1">Date</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {item.entries.slice(0, 10).map((entry, idx) => (
                                      <tr key={entry.bill_id ? `${entry.bill_id}-${idx}` : idx} className="text-zinc-400">
                                        <td className="py-1 font-mono text-xs">{entry.bill_id?.slice(0, 8)}...</td>
                                        <td className="py-1 text-right">{formatNumber(entry.qty)}</td>
                                        <td className="py-1 text-right text-xs">{formatDateTime(entry.created_at)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Ingredients;
